/**
 * PlanLimitsCard Component (Story 2.9, AC #7; Story 2.10)
 *
 * Displays current usage vs plan limits for all gated features
 * on the billing dashboard. Shows visual progress indicators and
 * an "Upgrade" CTA when approaching or at limits.
 */

import { useNavigate } from "@tanstack/react-router";
import { Card, CardContent, Button } from "@provenleap/ui";
import { usePlanLimits } from "../../hooks/usePlanLimits";
import { PLAN_HIERARCHY } from "@provenleap/types";

interface PlanLimitsCardProps {
  clientCount: number;
  teamMemberCount: number;
}

interface LimitRow {
  label: string;
  current: number;
  /** null = unlimited (paid tiers); progress bar is hidden in that case. */
  limit: number | null;
}

export function PlanLimitsCard({
  clientCount,
  teamMemberCount,
}: PlanLimitsCardProps) {
  const { plan, limits } = usePlanLimits();
  const navigate = useNavigate();

  const isTopPlan = plan === PLAN_HIERARCHY[PLAN_HIERARCHY.length - 1];

  const countRows: LimitRow[] = [
    { label: "Clients", current: clientCount, limit: limits.clientLimit },
    {
      label: "Team Members",
      current: teamMemberCount,
      limit: limits.teamMembers,
    },
  ];

  // Check if any feature is at or approaching limit (>80%).
  // null = unlimited → never needs upgrade for that row.
  const needsUpgrade = countRows.some(
    (row) => row.limit !== null && row.limit > 0 && row.current / row.limit >= 0.8
  );

  return (
    <Card data-testid="plan-limits-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-muted-foreground">
            Plan Limits
          </h3>
          {needsUpgrade && !isTopPlan && (
            <Button
              size="sm"
              onClick={() =>
                navigate({
                  to: "/settings/billing",
                  search: { upgrade: "true" },
                })
              }
            >
              Upgrade
            </Button>
          )}
        </div>

        {/* Count-based features with progress bars */}
        {countRows.map((row) => {
          const isUnlimited = row.limit === null;
          const percentage =
            !isUnlimited && row.limit !== null && row.limit > 0
              ? Math.min(Math.round((row.current / row.limit) * 100), 100)
              : 0;
          const isAtLimit = !isUnlimited && row.limit !== null && row.current >= row.limit;

          return (
            <div key={row.label} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>{row.label}</span>
                <span className={isAtLimit ? "text-destructive font-medium" : ""}>
                  {row.current} / {isUnlimited ? "Unlimited" : row.limit}
                </span>
              </div>
              <div
                role="progressbar"
                aria-valuenow={isUnlimited ? 0 : percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${row.label} usage`}
                className="h-2 rounded-full bg-muted overflow-hidden"
              >
                <div
                  className={`h-full rounded-full transition-all ${
                    isAtLimit
                      ? "bg-destructive"
                      : percentage >= 80
                        ? "bg-yellow-500"
                        : "bg-primary"
                  }`}
                  style={{ width: isUnlimited ? "0%" : `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}

        {/* Non-count features with status badges */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Branding</span>
            <p className="font-medium flex items-center gap-1.5">
              {limits.pdfBranding ? "Your brand" : "ProvenLeap"}
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  limits.pdfBranding ? "bg-primary" : "bg-muted-foreground"
                }`}
                aria-hidden="true"
              />
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Custom Domain</span>
            <p className="font-medium flex items-center gap-1.5">
              {limits.customDomain ? "Enabled" : "—"}
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  limits.customDomain ? "bg-primary" : "bg-muted-foreground"
                }`}
                aria-hidden="true"
              />
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">REST API</span>
            <p className="font-medium flex items-center gap-1.5">
              {limits.restApiAccess ? "Enabled" : "—"}
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  limits.restApiAccess ? "bg-primary" : "bg-muted-foreground"
                }`}
                aria-hidden="true"
              />
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
